"use client";

import { Component, type ReactNode } from "react";
import { reportError } from "@/lib/observability";

interface FeatureBoundaryProps {
  /** Identifies the feature in telemetry, e.g. "editor", "board", "chat". */
  name: string;
  /**
   * Fallback when the subtree crashes:
   *  - omitted  → small inline "section failed, retry" card
   *  - `null`   → render nothing (use for non-critical widgets/banners)
   *  - element  → static fallback
   *  - function → receives a `reset` callback to re-mount the subtree
   */
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
  children: ReactNode;
}

interface FeatureBoundaryState {
  hasError: boolean;
}

/**
 * Feature-scoped React error boundary. A crash here is contained to this
 * subtree — the rest of the app keeps rendering — and reported via the
 * observability seam. This is the building block for failure isolation:
 * wrap the editor, board, chat, modals, and any non-critical widget.
 */
export class FeatureBoundary extends Component<FeatureBoundaryProps, FeatureBoundaryState> {
  state: FeatureBoundaryState = { hasError: false };

  static getDerivedStateFromError(): FeatureBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string }) {
    reportError(error, {
      scope: `feature:${this.props.name}`,
      feature: this.props.name,
      componentStack: info.componentStack,
    });
  }

  reset = () => this.setState({ hasError: false });

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (fallback === null) return null;
    if (typeof fallback === "function") return fallback(this.reset);
    if (fallback !== undefined) return fallback;

    return (
      <div
        role="alert"
        className="m-2 flex flex-col items-center justify-center gap-1.5 rounded-md border border-component bg-background p-4 text-sm"
      >
        <p className="font-medium text-gray-900 dark:text-gray-100">This section failed to load.</p>
        <p className="text-gray-500 dark:text-gray-400">
          {/* The rest of the app is still working — you can retry just this part. */}
        </p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover-component dark:border-zinc-600 dark:text-gray-300 "
        >
          Retry
        </button>
      </div>
    );
  }
}

export default FeatureBoundary;
